import { Component, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SwUpdate } from '@angular/service-worker';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { VersionService } from '../services/version.service';
import { CheckForUpdateService } from '../services/check-for-update.service';
import * as Sentry from '@sentry/angular-ivy';
import { environment } from '../../environments/environment';
import { MatSnackBar } from '@angular/material/snack-bar';
import { EventService } from '../services/event.service';
import { Subscription } from 'rxjs';
import { PhoneService } from '../services/phone.service';
import { Router } from '@angular/router';
import { LoggingService } from '../services/logging.service';
import { AppUrls } from '../app.urls';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';

// import translations, these are compiled-in, they used to be loaded via HTTP requests, but this proved error-prone
import deTranslation from '../../assets/i18n/de.json';
import frTranslation from '../../assets/i18n/fr.json';
import itTranslation from '../../assets/i18n/it.json';
import enTranslation from '../../assets/i18n/en.json';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {

  private toolbarSubscriptions: Subscription[];

  constructor(private translate: TranslateService,
              private swUpdate: SwUpdate,
              private snackBar: MatSnackBar,
              private auth: AngularFireAuth,
              private version: VersionService,
              private events: EventService,
              private phone: PhoneService,
              private router: Router,
              private logger: LoggingService,
              private matIconRegistry: MatIconRegistry,
              private domSanitizer: DomSanitizer,
              private checkForUpdate: CheckForUpdateService) {
    this.addCustomIcons();
  }

  ngOnInit(): void {
    const supportedLangs = ['de', 'fr', 'it', 'en'];
    this.translate.setDefaultLang(supportedLangs[0]);
    this.translate.addLangs(supportedLangs);
    this.translate.setTranslation('de', deTranslation);
    this.translate.setTranslation('fr', frTranslation);
    this.translate.setTranslation('it', itTranslation);
    this.translate.setTranslation('en', enTranslation);

    const browserLang = this.translate.getBrowserLang();
    this.translate.use(supportedLangs.includes(browserLang) ? browserLang : supportedLangs[0]);

    // set sentry user context
    if (environment.sentryDsn) {
      this.auth.user.subscribe(u => {
        if (u) {
          Sentry.setUser({
            email: u.email,
            id: u.uid
          });
        }
      });
    }

    // Prompt the user to update if the app was updated (PWA)
    this.swUpdate.available.subscribe(() => {
      const snackBarRef = this.snackBar.open(
        this.translate.instant('update.newVersionAvailable'),
        this.translate.instant('update.reload'), {
          duration: 10000,
          verticalPosition: 'bottom',
          horizontalPosition: 'end'
        });
      snackBarRef.onAction().subscribe(() => {
        window.location.reload();
      });
    });

    this.checkForUpdate.startCheckingForUpdates();

    // Subscribe to toolbar events
    this.toolbarSubscriptions = [
      this.events.callButtonClicked.subscribe(() => this.handleCallButtonClicked()),
      this.events.settingsButtonClicked.subscribe(() => this.handleSettingsButtonClicked()),
      this.events.refreshButtonClicked.subscribe(() => this.handleRefreshButtonClicked())
    ];
  }

  ngOnDestroy(): void {
    this.toolbarSubscriptions.forEach(sub => sub.unsubscribe());
  }

  private handleCallButtonClicked(): void {
    this.phone.callSupport();
    this.logger.log('👆🏻 CallButtonClicked');
  }

  private handleSettingsButtonClicked(): void {
    this.router.navigate([AppUrls.ROOT, AppUrls.Settings]);
    this.logger.log('👆🏻 SettingsButtonClicked');
  }

  private handleRefreshButtonClicked(): void {
    this.logger.log('👆🏻 RefreshButtonClicked');
  }

  private addCustomIcons(): void {
    this.matIconRegistry.addSvgIcon('golf',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/images/golf.svg'));
    this.matIconRegistry.addSvgIcon('specialbike',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/images/specialbike.svg'));
    this.matIconRegistry.addSvgIcon('subsequent-deliveries',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/images/subsequent-deliveries.svg'));
    this.matIconRegistry.addSvgIcon('bicycle-small',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/images/sbb/bicycle-small.svg'));
    this.matIconRegistry.addSvgIcon('suitcase-small',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/images/sbb/suitcase-small.svg'));
    this.matIconRegistry.addSvgIcon('envelope-small',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/images/sbb/envelope-small.svg'));
    this.matIconRegistry.addSvgIcon('bike-profile-power-plug-small',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/images/sbb/bike-profile-power-plug-small.svg'));
    this.matIconRegistry.addSvgIcon('skis-ski-poles-small',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/images/sbb/skis-ski-poles-small.svg'));
  }
}
